import { Box, Divider, Grid, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import {
  GET_LIST,
  FunctionField,
  ReferenceField,
  Show,
  ShowProps,
  SimpleShowLayout,
  TextField,
  sanitizeFetchType,
  useMutation,
  useNotify,
  useRefresh,
  useShowContext,
  useTranslate,
  useQueryWithStore,
  TabbedShowLayout,
  Tab,
  Labeled,
} from 'react-admin'
import ReactJson from 'react-json-view'
import { TicketImportDto } from '../../core/dto/ticket-import.dto'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { ResourceName } from '../../core/ResourceName'
import Button from '../common/customized-mui-components/Button'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { Authority } from '../../core/auth/Authority'
import { ExtendedTheme } from '../layout/app.theme'
import { TicketImportStates } from '../../core/enum/TicketImportStates'
import { TicketImportUpdateButton } from './TicketImportUpdateButton'
import { TicketImportUpdateDto } from '../../core/dto/ticket-import-update.dto'

const ShowTitle = ({ ...props }) => {
  const { record: ticketImport } = props
  const translate = useTranslate()

  return (
    <span>
      {translate('resources.ticket-imports.titles.show')}: {ticketImport?.name}
    </span>
  )
}

const TicketImportRevertButton = ({ ...props }) => {
  const { record: ticketImport, loading } = useShowContext<TicketImportDto>()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const notify = useNotify()
  const hasAuthority = useHasAuthority()
  const refresh = useRefresh()

  const [confirmDialogOpen, setCofirmDialogOpen] = useState<boolean>(false)

  const handleRevertImport = async () => {
    await mutate(
      {
        type: sanitizeFetchType('revertTicketsAsyncJob'),
        resource: ResourceName.TICKET_IMPORTS,
        payload: {
          importId: ticketImport?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (msg) => notify(msg, 'error'),
      },
    )
    refresh()
  }

  const handleConfirmDialogOpen = () => {
    setCofirmDialogOpen(true)
  }

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        confirm={handleRevertImport}
        source="revertTicketsAsyncJob"
        cancel={() => setCofirmDialogOpen(false)}
        action="set"
        resource={ResourceName.TICKET_IMPORTS}
      />
      <Button
        onClick={handleConfirmDialogOpen}
        label="resources.ticket-imports.actions.revertTicketsAsyncJob"
        {...props}
        useSmallVersionBreakpoint={false}
        disabled={
          !hasAuthority(Authority.REVERT_TICKET_IMPORT) ||
          loading ||
          mutationLoading ||
          ticketImport?.state !== TicketImportStates.IMPORTED
        }
        variant="contained"
        color="primary"
        size="small"
      />
    </>
  )
}

const TicketImportAcceptButton = ({ ...props }) => {
  const { record: ticketImport, loading } = useShowContext<TicketImportDto>()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const notify = useNotify()
  const hasAuthority = useHasAuthority()
  const refresh = useRefresh()

  const [confirmDialogOpen, setCofirmDialogOpen] = useState<boolean>(false)

  const handleAcceptImport = async () => {
    await mutate(
      {
        type: sanitizeFetchType('acceptTicketsImportAsyncJob'),
        resource: ResourceName.TICKET_IMPORTS,
        payload: {
          importId: ticketImport?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (msg) => notify(msg, 'error'),
      },
    )
    refresh()
  }

  const handleConfirmDialogOpen = () => {
    setCofirmDialogOpen(true)
  }

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        confirm={handleAcceptImport}
        source="acceptTicketsImportAsyncJob"
        cancel={() => setCofirmDialogOpen(false)}
        action="set"
        resource={ResourceName.TICKET_IMPORTS}
      />
      <Button
        onClick={handleConfirmDialogOpen}
        label="resources.ticket-imports.actions.acceptTicketsImportAsyncJob"
        {...props}
        useSmallVersionBreakpoint={false}
        disabled={
          !hasAuthority(Authority.ACCEPT_TICKET_IMPORT) ||
          loading ||
          mutationLoading ||
          ticketImport?.state !== TicketImportStates.IMPORTED
        }
        variant="contained"
        color="primary"
        size="small"
      />
    </>
  )
}

const GeneralActions = () => {
  const { record: ticketImport } = useShowContext<TicketImportDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <TicketImportUpdateButton
              buttonProps={{
                disabled:
                  !hasAuthority(Authority.IMPORT_TICKETS) ||
                  ticketImport?.state !== TicketImportStates.IMPORTED,
              }}
              dialogProps={{ importId: ticketImport?.id }}
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <TicketImportRevertButton
              disabled={!hasAuthority(Authority.REVERT_TICKET_IMPORT)}
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <TicketImportAcceptButton
              disabled={!hasAuthority(Authority.ACCEPT_TICKET_IMPORT)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const TicketImportUpdatesTab = ({ ticketImportUpdatesQuery, ...props }) => {
  const { disabled } = props
  const translate = useTranslate()
  const theme = useTheme<ExtendedTheme>()

  return (
    <Tab
      {...props}
      label="resources.ticket-imports.tabs.updates"
      path="updates"
    >
      {!disabled && (
        <Box p={2}>
          {ticketImportUpdatesQuery.map((update: any) => (
            <Box
              key={update.id}
              mb={2}
              p={2}
              border={`1px solid ${theme.palette.divider}`}
              borderRadius={theme.shape.borderRadius}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2}>
                  <Labeled
                    label={translate(
                      'resources.ticket-import-updates.fields.id',
                    )}
                  >
                    <ReferenceField
                      source="id"
                      record={update}
                      reference={ResourceName.TICKET_IMPORT_UPDATES}
                      link="show"
                    >
                      <TextField source="id" />
                    </ReferenceField>
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Labeled
                    label={translate(
                      'resources.ticket-import-updates.fields.jobId',
                    )}
                  >
                    <ReferenceField
                      record={update}
                      source="jobId"
                      reference={ResourceName.JOBS}
                      link="show"
                    >
                      <TextField record={update} source="jobId" />
                    </ReferenceField>
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Labeled
                    label={translate(
                      'resources.ticket-import-updates.fields.name',
                    )}
                  >
                    <TextField record={update} source="name" />
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Labeled
                    label={translate(
                      'resources.ticket-import-updates.fields.originalFileName',
                    )}
                  >
                    <TextField record={update} source="originalFileName" />
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Labeled
                    label={translate(
                      'resources.ticket-import-updates.fields.state',
                    )}
                  >
                    <FunctionField<TicketImportUpdateDto>
                      source="state"
                      record={update}
                      render={(record?: TicketImportUpdateDto) => (
                        <div>
                          {translate(
                            `resources.enums.TicketImportUpdateStates.${record?.state}`,
                          )}
                        </div>
                      )}
                    />
                  </Labeled>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      )}
    </Tab>
  )
}

const TicketImportGeneralTab = ({ ...props }) => {
  const translate = useTranslate()
  const theme = useTheme<ExtendedTheme>()
  const { loaded, loading, record: ticketImport } = useShowContext()

  return (
    <Tab {...props} label="resources.ticket-imports.tabs.general">
      <SimpleShowLayout {...props}>
        <TextField source="id" label="Id" />
        <TextField source="name" />
        <TextField source="originalFileName" />
        <FunctionField<TicketImportDto>
          source="state"
          render={(r) => (
            <div>
              {translate(`resources.enums.TicketImportStates.${r?.state}`)}
            </div>
          )}
        />
        <ReferenceField
          source="lastJobId"
          reference={ResourceName.JOBS}
          link="show"
        >
          <TextField source="jobId" />
        </ReferenceField>
        {loaded &&
          !loading &&
          ticketImport?.templateSnapshot &&
          typeof ticketImport.templateSnapshot === 'object' && (
            <FunctionField<TicketImportDto>
              source="templateSnapshot"
              label=" "
              render={(r) =>
                r?.templateSnapshot &&
                typeof r.templateSnapshot === 'object' ? (
                  <ReactJson
                    src={r.templateSnapshot}
                    theme={theme.palette.rjvTheme}
                    iconStyle="triangle"
                    name={translate(
                      'resources.ticket-imports.fields.templateSnapshot',
                    )}
                    style={{
                      marginBottom: theme.spacing(0.5),
                      padding: theme.spacing(0.5),
                      fontSize: '0.85rem',
                      lineHeight: 1.2,
                    }}
                    displayObjectSize={false}
                    displayDataTypes={false}
                    quotesOnKeys={false}
                    collapsed={1}
                  />
                ) : null
              }
            />
          )}
        <GeneralActions />
      </SimpleShowLayout>
    </Tab>
  )
}

const TicketImportTabs = ({ ...props }) => {
  const { record: ticketImport } = useShowContext()

  const { data: ticketImportUpdatesQuery } = useQueryWithStore({
    type: sanitizeFetchType(GET_LIST),
    resource: ResourceName.TICKET_IMPORT_UPDATES,
    payload: { filter: { importId: ticketImport?.id } },
  })

  return (
    <TabbedShowLayout {...props}>
      <TicketImportGeneralTab />
      <TicketImportUpdatesTab
        ticketImportUpdatesQuery={ticketImportUpdatesQuery}
        disabled={
          !Array.isArray(ticketImportUpdatesQuery) ||
          ticketImportUpdatesQuery?.length < 1
        }
      />
    </TabbedShowLayout>
  )
}

export const TicketImportShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <TicketImportTabs />
  </Show>
)
